import React, { useMemo, useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AddCircleOutlineOutlinedIcon from "@material-ui/icons/AddCircleOutlineOutlined";
import CloseIcon from "@material-ui/icons/Close";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import CheckIcon from "@material-ui/icons/Check";
import UndoIcon from "@material-ui/icons/Undo";
import CancelIcon from "@material-ui/icons/Cancel";
import FontAwesomeIconComponent from "../Layouts/FontAwesomeIconComponent";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./DashboardCalendar.css";
import swal from "sweetalert";
import AuthApi from "../Services/Authapi";
import ls from "local-storage";
import { Checkbox } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { getCalendarColorSettings, fetchAndSyncCalendarColors, CALENDAR_COLORS_STORAGE_EVENT } from "../../utils/calendarColorSettings";

const localizer = momentLocalizer(moment);
const ALL_DAY_CATEGORIES = ["event", "holiday", "leave", "reminder", "calendar", "birthday", "bday"];

const createEmptyAddPopoverState = () => ({
  anchorPosition: null,
  start: null,
  end: null,
  formattedDate: "",
  mode: "create",
  eventId: null,
  eventMeta: null,
});

const createEmptyEventDetailsState = () => ({
  anchorPosition: null,
  event: null,
});

const hasExplicitTime = (value) => {
  if (!value) {
    return false;
  }

  const parsed = moment(value);
  return parsed.isValid() && (
    parsed.hours() !== 0 ||
    parsed.minutes() !== 0 ||
    parsed.seconds() !== 0 ||
    parsed.milliseconds() !== 0
  );
};

const parseCalendarDateValue = (value, treatAsAllDayDate = false) => {
  if (!value) {
    return moment.invalid();
  }

  if (moment.isMoment(value)) {
    return value.clone();
  }

  if (value instanceof Date) {
    return moment(value);
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return moment.invalid();
    }

    const isoDateMatch = trimmedValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (treatAsAllDayDate && isoDateMatch) {
      return moment(isoDateMatch[1], "YYYY-MM-DD", true);
    }

    return moment(trimmedValue);
  }

  return moment(value);
};

const getEventTitleValue = (event = {}) => (
  event.Subject ||
  event.title ||
  event.name ||
  event.eventName ||
  event.upcomingEventName ||
  ""
);

const normalizeEventDates = (event, index) => {
  const startSource = event.StartTime || event.start || event.date;
  const endSource = event.EndTime || event.end || startSource;
  const rawAllDayFlag = event.IsAllDay ?? event.isAllDay ?? event.is_all_day;
  const treatAsAllDayDate = Boolean(rawAllDayFlag);
  const startMoment = parseCalendarDateValue(startSource, treatAsAllDayDate);
  const endMoment = parseCalendarDateValue(endSource, treatAsAllDayDate);

  if (!startMoment.isValid()) {
    return null;
  }

  const rawCategory = String(event.Category || event.category || "event").toLowerCase();
  const rawTitleLower = String(getEventTitleValue(event)).trim().toLowerCase();

  let normalizedCategory = rawCategory;
  if (
    rawCategory === "reminder" ||
    rawCategory === "calendar" ||
    rawCategory === "event" ||
    rawCategory === "title" ||
    !rawCategory
  ) {
    if (
      rawTitleLower === "meeting" ||
      rawTitleLower.startsWith("meeting -") ||
      rawTitleLower.startsWith("meeting:") ||
      rawTitleLower.includes("meeting")
    ) {
      normalizedCategory = "meeting";
    } else if (
      rawTitleLower === "leave" ||
      rawTitleLower.startsWith("leave -") ||
      rawTitleLower.startsWith("leave:") ||
      rawTitleLower.includes("leave") ||
      rawTitleLower.includes("sick leave") ||
      rawTitleLower.includes("casual leave") ||
      rawTitleLower.includes("loss of pay")
    ) {
      normalizedCategory = "leave";
    }
  }

  let inferredAllDay = typeof event.IsAllDay !== "undefined"
    ? Boolean(event.IsAllDay)
    : ALL_DAY_CATEGORIES.includes(normalizedCategory) || (!hasExplicitTime(startSource) && !hasExplicitTime(endSource));
  const forceSingleDayRender = normalizedCategory === "event" || normalizedCategory === "holiday";

  let start = startMoment.clone();
  let end = endMoment.isValid() ? endMoment.clone() : startMoment.clone();

  if (forceSingleDayRender) {
    start = start.startOf("day");
    end = start.clone().add(30, "minutes");
    inferredAllDay = false;
  } else if (inferredAllDay) {
    const spansMultipleDays = end.isAfter(start, "day");
    start = start.startOf("day");

    if (!spansMultipleDays) {
      end = start.clone().endOf("day");
    } else {
      end = end.endOf("day");
    }
  } else if (!end.isAfter(start)) {
    end = start.clone().add(1, "hour");
  }

  return {
    ...event,
    id: `${normalizedCategory}-${event.Id ?? event.id ?? "item"}-${getEventTitleValue(event) || "untitled"}-${start.valueOf()}-${index}`,
    title: getEventTitleValue(event) || "Untitled",
    start: start.toDate(),
    end: end.toDate(),
    allDay: inferredAllDay,
    category: normalizedCategory,
    resource: event,
  };
};

const getFormattedTitleWithCategory = (event = {}) => {
  const originalEvent = event?.resource || event || {};
  let rawTitle = String(
    event?.title ||
    getEventTitleValue(originalEvent) ||
    "Untitled"
  ).trim();

  let category = String(
    event?.category ||
    originalEvent.Category ||
    originalEvent.category ||
    ""
  ).trim().toLowerCase();

  let titleLower = rawTitle.toLowerCase();

  if (titleLower.startsWith("title - ") || titleLower.startsWith("reminder - ") || titleLower.startsWith("calendar - ")) {
    const prefixLength = titleLower.indexOf(" - ") + 3;
    const stripped = rawTitle.substring(prefixLength).trim();
    const strippedLower = stripped.toLowerCase();
    if (
      strippedLower === "leave" ||
      strippedLower.startsWith("leave -") ||
      strippedLower.startsWith("leave:") ||
      strippedLower.includes("leave") ||
      strippedLower.includes("sick leave") ||
      strippedLower.includes("casual leave") ||
      strippedLower.includes("loss of pay")
    ) {
      rawTitle = stripped;
      titleLower = strippedLower;
      category = "leave";
    } else if (
      category === "meeting" ||
      strippedLower === "meeting" ||
      strippedLower.startsWith("meeting -") ||
      strippedLower.startsWith("meeting:") ||
      strippedLower.includes("meeting") ||
      strippedLower.includes("scrum")
    ) {
      rawTitle = stripped;
      titleLower = strippedLower;
      category = "meeting";
    }
  }

  if (
    category === "meeting" ||
    titleLower === "meeting" ||
    titleLower.startsWith("meeting -") ||
    titleLower.startsWith("meeting:") ||
    titleLower.includes("meeting") ||
    titleLower.includes("scrum")
  ) {
    category = "meeting";
  } else if (
    category === "leave" ||
    titleLower === "leave" ||
    titleLower.startsWith("leave -") ||
    titleLower.startsWith("leave:") ||
    titleLower.includes("leave") ||
    titleLower.includes("sick leave") ||
    titleLower.includes("casual leave") ||
    titleLower.includes("loss of pay")
  ) {
    category = "leave";
  }

  let label = "Event";
  if (category === "meeting") {
    label = "Meeting";
  } else if (category === "holiday") {
    label = "Holiday";
  } else if (category === "leave") {
    label = "Leave";
  } else if (category === "reminder" || category === "calendar" || category === "title") {
    label = "Title";
  } else if (category === "birthday" || category === "bday") {
    label = "Birthday";
  } else if (category === "event") {
    label = "Event";
  }

  const knownLabels = ["Event", "Holiday", "Leave", "Reminder", "Calendar", "Birthday", "Meeting", "Title"];
  const hasKnownPrefix = knownLabels.some((l) =>
    titleLower.startsWith(`${l.toLowerCase()} - `) ||
    titleLower.startsWith(`${l.toLowerCase()}: `)
  );

  if (hasKnownPrefix) {
    if ((category === "meeting" || titleLower.includes("scrum") || titleLower.includes("meeting")) && (titleLower.startsWith("reminder - ") || titleLower.startsWith("title - ") || titleLower.startsWith("calendar - "))) {
      const prefixLength = titleLower.indexOf(" - ") + 3;
      const stripped = rawTitle.substring(prefixLength).trim();
      return stripped.toLowerCase() === "meeting" ? "Meeting" : `Meeting - ${stripped}`;
    }
    if (category === "leave" && (titleLower.startsWith("reminder - ") || titleLower.startsWith("title - "))) {
      const prefixLength = titleLower.startsWith("reminder - ") ? 11 : 8;
      const stripped = rawTitle.substring(prefixLength).trim();
      return stripped.toLowerCase() === "leave" ? "Leave" : `Leave - ${stripped}`;
    }
    return rawTitle;
  }

  if (titleLower === label.toLowerCase()) {
    return label;
  }

  return `${label} - ${rawTitle}`;
};

const formatEventTooltip = (event) => {
  const title = getFormattedTitleWithCategory(event);
  const originalEvent = event.resource || {};
  const startSource = originalEvent.StartTime || originalEvent.start || originalEvent.date || originalEvent.eventdate || originalEvent.upcomingEventdate || originalEvent.upcoming_event_date;
  const hasExplicitEndTime = Boolean(originalEvent.EndTime || originalEvent.end || originalEvent.endDate);
  const endSource = hasExplicitEndTime ? (originalEvent.EndTime || originalEvent.end || originalEvent.endDate) : startSource;
  const rawAllDayFlag = originalEvent.IsAllDay ?? originalEvent.isAllDay ?? originalEvent.is_all_day ?? event.allDay;
  const treatAsAllDayDate = Boolean(rawAllDayFlag);
  const startMoment = parseCalendarDateValue(startSource || event.start, treatAsAllDayDate);
  const endMoment = parseCalendarDateValue(endSource || event.end, treatAsAllDayDate);
  const hasOriginalTime = hasExplicitTime(startSource) || hasExplicitTime(endSource);

  if (!startMoment.isValid()) {
    return title;
  }

  const dateLabel = startMoment.format("MMMM D, YYYY");
  const isSameDay = !hasExplicitEndTime || !endMoment.isValid() || startMoment.isSame(endMoment, "day") || (endMoment.clone().subtract(1, "second").isSame(startMoment, "day")) || (endMoment.diff(startMoment, "hours") <= 24);

  if (event.allDay || !hasOriginalTime) {
    if (isSameDay) {
      return `${title}\n${dateLabel}`;
    }

    const formattedEndDay = endMoment.clone().subtract(1, "second").format("MMMM D, YYYY");
    return `${title}\n${startMoment.format("MMMM D, YYYY")} - ${formattedEndDay}`;
  }

  const startTimeLabel = startMoment.format("h:mm A");
  const endTimeLabel = endMoment.isValid() ? endMoment.format("h:mm A") : startMoment.clone().add(30, "minutes").format("h:mm A");

  if (isSameDay) {
    return `${title}\n${dateLabel}\n${startTimeLabel} - ${endTimeLabel}`;
  }

  return `${title}\n${startMoment.format("MMMM D, YYYY h:mm A")} - ${endMoment.format("MMMM D, YYYY h:mm A")}`;
};

const formatEventDisplayTitle = (event) => {
  const originalEvent = event.resource || {};
  const startSource = originalEvent.StartTime || originalEvent.start || originalEvent.date;
  const rawAllDayFlag = originalEvent.IsAllDay ?? originalEvent.isAllDay ?? originalEvent.is_all_day ?? event.allDay;
  const formattedTitle = getFormattedTitleWithCategory(event);

  if (Boolean(rawAllDayFlag) || !hasExplicitTime(startSource)) {
    return formattedTitle;
  }

  const startMoment = parseCalendarDateValue(startSource, false);
  if (!startMoment.isValid()) {
    return formattedTitle;
  }

  return `${startMoment.format("h:mm A")} ${formattedTitle}`;
};

const getComparableCalendarCategory = (event = {}) => {
  const normalizedCategory = String(event.Category || event.category || event.categoryName || "event").trim().toLowerCase();
  return normalizedCategory === "reminder" ? "calendar" : normalizedCategory;
};

const getComparableCalendarEventId = (event = {}) => {
  const explicitId = event.calendar_event_id ?? event.calendarEventId ?? event.event_id ?? event.eventId ?? event.Id ?? event.id ?? null;

  if (explicitId === null || typeof explicitId === "undefined") {
    return "";
  }

  return String(explicitId).trim();
};

const normalizeComparableCalendarDate = (value) => {
  if (!value) {
    return "";
  }

  const parsedValue = parseCalendarDateValue(value, false);
  if (parsedValue.isValid()) {
    return parsedValue.toISOString();
  }

  return String(value).trim();
};

const getComparableCalendarEventKey = (event = {}) => {
  const category = getComparableCalendarCategory(event);
  const explicitId = getComparableCalendarEventId(event);
  const title = String(getEventTitleValue(event)).trim().toLowerCase();
  const startSource = event.StartTime || event.start || event.date || "";
  const endSource = event.EndTime || event.end || event.date || startSource;
  const startMoment = parseCalendarDateValue(startSource, false);
  const endMoment = parseCalendarDateValue(endSource, false);
  const normalizedDay = startMoment.isValid() ? startMoment.format("YYYY-MM-DD") : normalizeComparableCalendarDate(startSource);
  const normalizedEndDay = endMoment.isValid() ? endMoment.format("YYYY-MM-DD") : normalizeComparableCalendarDate(endSource);

  if (explicitId) {
    return [category || "item", explicitId].join("|");
  }

  return [
    category || "item",
    title || "untitled",
    normalizedDay || "no-start",
    normalizedEndDay || "no-end",
  ].join("|");
};

const normalizeBackendEntityId = (value) => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    if (/^\d+$/.test(trimmedValue)) {
      return Number(trimmedValue);
    }

    const trailingNumericMatch = trimmedValue.match(/-(\d+)$/);
    if (trailingNumericMatch) {
      return Number(trailingNumericMatch[1]);
    }
  }

  return value ?? null;
};

const getEventIdentifier = (event = {}) => (
  normalizeBackendEntityId(
    event.Id ??
    event.id ??
    event.event_id ??
    event.eventId ??
    event.calendar_event_id ??
    event.calendarEventId ??
    null
  )
);

const getEventDeleteMeta = (event = {}, normalizedCategory = "") => {
  const category = String(normalizedCategory || event.Category || event.category || "").trim().toLowerCase();
  const titleLower = String(getEventTitleValue(event) || "").trim().toLowerCase();

  if (
    category === "birthday" ||
    category === "bday" ||
    titleLower.startsWith("birthday") ||
    titleLower.includes("birthday") ||
    titleLower.includes("bday")
  ) {
    return null;
  }

  const calendarEventId = normalizeBackendEntityId(event.calendar_event_id ?? event.calendarEventId ?? null);
  const standardEventId = normalizeBackendEntityId(event.event_id ?? event.eventId ?? event.Id ?? event.id ?? null);
  const holidayId = normalizeBackendEntityId(
    event.holiday_id ??
    event.holidayId ??
    event.leave_id ??
    event.leaveId ??
    event.Id ??
    event.id ??
    null
  );
  const leaveId = normalizeBackendEntityId(
    event.SourceId ??
    event.leave_id ??
    event.leaveId ??
    event.Id ??
    event.id ??
    null
  );
  const looksLikeDashboardCalendarItem = Boolean(
    event.StartTime ||
    event.EndTime ||
    typeof event.showToAll !== "undefined" ||
    typeof event.show_to_all !== "undefined" ||
    typeof event.ShowToAll !== "undefined" ||
    typeof event.IsAllDay !== "undefined" ||
    typeof event.isAllDay !== "undefined" ||
    typeof event.is_all_day !== "undefined" ||
    typeof event.created_by !== "undefined" ||
    typeof event.createdBy !== "undefined"
  );

  if (category === "leave" && leaveId) {
    return {
      strategy: "leave",
      id: leaveId,
    };
  }

  if (calendarEventId) {
    return {
      strategy: "calendar",
      id: calendarEventId,
    };
  }

  if ((category === "calendar" || category === "reminder") && standardEventId) {
    return {
      strategy: "calendar",
      id: standardEventId,
    };
  }

  if (category === "holiday" && holidayId) {
    return {
      strategy: "holiday",
      id: holidayId,
    };
  }

  if ((category === "event" || category === "upcoming") && standardEventId) {
    return {
      strategy: "event",
      id: standardEventId,
    };
  }

  if (looksLikeDashboardCalendarItem && standardEventId) {
    return {
      strategy: "calendar",
      id: standardEventId,
    };
  }

  if (standardEventId) {
    return {
      strategy: "event",
      id: standardEventId,
    };
  }

  return null;
};

const getEventUpdateMeta = (event = {}, normalizedCategory = "") => {
  const category = String(normalizedCategory || event.Category || event.category || "").trim().toLowerCase();
  const titleLower = String(getEventTitleValue(event) || "").trim().toLowerCase();

  if (
    category === "birthday" ||
    category === "bday" ||
    titleLower.startsWith("birthday") ||
    titleLower.includes("birthday") ||
    titleLower.includes("bday")
  ) {
    return null;
  }

  const deleteMeta = getEventDeleteMeta(event, normalizedCategory);

  if (deleteMeta && ["holiday", "event", "upcoming"].includes(deleteMeta.strategy)) {
    return deleteMeta;
  }

  if (category === "calendar" || (deleteMeta && deleteMeta.strategy === "calendar")) {
    return {
      strategy: "calendar",
      id: getEventIdentifier(event),
    };
  }

  return deleteMeta;
};

const isDeleteSupportedForMeta = (deleteMeta) => {
  if (!deleteMeta || !deleteMeta.strategy) {
    return false;
  }

  return ["event", "upcoming", "calendar", "holiday", "leave"].includes(deleteMeta.strategy);
};

const getBooleanFlag = (value, defaultValue = false) => {
  if (typeof value === "undefined" || value === null || value === "") {
    return defaultValue;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  return ["1", "true", "yes"].includes(String(value).trim().toLowerCase());
};

const formatPopoverDateText = (start, end) => {
  const startMoment = moment(start);
  const rawEndMoment = end ? moment(end) : moment(start);
  const hasSingleDaySelection = !end || rawEndMoment.diff(startMoment, "days") <= 1;

  if (hasSingleDaySelection) {
    return startMoment.format("MMMM D, YYYY");
  }

  const adjustedEnd = rawEndMoment.clone().subtract(1, "day");
  if (startMoment.year() === adjustedEnd.year()) {
    if (startMoment.month() === adjustedEnd.month()) {
      return `${startMoment.format("MMMM D")} - ${adjustedEnd.format("D, YYYY")}`;
    }

    return `${startMoment.format("MMMM D")} - ${adjustedEnd.format("MMMM D, YYYY")}`;
  }

  return `${startMoment.format("MMMM D, YYYY")} - ${adjustedEnd.format("MMMM D, YYYY")}`;
};

const getCategoryDisplayLabel = (category = "") => {
  const normalizedCategory = String(category || "").trim().toLowerCase();

  if (normalizedCategory === "meeting") {
    return "Meeting";
  }

  if (normalizedCategory === "holiday") {
    return "Holiday";
  }

  if (normalizedCategory === "event") {
    return "Event";
  }

  if (normalizedCategory === "reminder" || normalizedCategory === "title") {
    return "Title";
  }

  if (normalizedCategory === "leave") {
    return "Leave";
  }

  if (normalizedCategory === "timeentry" || normalizedCategory === "time") {
    return "Time Entry";
  }

  if (normalizedCategory === "calendar") {
    return "Calendar";
  }

  if (normalizedCategory === "birthday" || normalizedCategory === "bday") {
    return "Birthday";
  }

  return category ? String(category).toUpperCase() : "Item";
};

const VIEW_OPTIONS = [
  { label: "Day", value: Views.DAY },
  { label: "Week", value: Views.WEEK },
  { label: "Work Week", value: Views.WORK_WEEK },
  { label: "Month", value: Views.MONTH },
  { label: "Agenda", value: Views.AGENDA },
];

const getToolbarLabel = (date, view) => {
  const currentMoment = moment(date);

  if (view === Views.DAY) {
    return currentMoment.format("MMMM DD, YYYY");
  }

  if (view === Views.WEEK || view === Views.WORK_WEEK) {
    const weekStart = currentMoment.clone().startOf(view === Views.WORK_WEEK ? "isoWeek" : "week");
    const weekEnd = weekStart.clone().add(view === Views.WORK_WEEK ? 4 : 6, "days");

    if (weekStart.month() === weekEnd.month()) {
      return `${weekStart.format("MMMM DD")} - ${weekEnd.format("DD, YYYY")}`;
    }

    if (weekStart.year() === weekEnd.year()) {
      return `${weekStart.format("MMMM DD")} - ${weekEnd.format("MMMM DD, YYYY")}`;
    }

    return `${weekStart.format("MMMM DD, YYYY")} - ${weekEnd.format("MMMM DD, YYYY")}`;
  }

  if (view === Views.AGENDA) {
    return `${currentMoment.format("MMMM YYYY")} Agenda`;
  }

  return currentMoment.format("MMMM YYYY");
};

const CustomToolbar = ({
  date,
  onNavigate,
  view,
  onOpenViewMenu,
}) => (
  <div className="dashboard-calendar-rbc-toolbar">
    <div className="dashboard-calendar-toolbar-left">
      <div className="dashboard-calendar-nav-group">
        <button
          type="button"
          className="dashboard-calendar-nav-btn"
          onClick={() => onNavigate("PREV")}
          aria-label="Previous"
        >
          <ChevronLeftIcon fontSize="small" />
        </button>
        <button
          type="button"
          className="dashboard-calendar-nav-btn"
          onClick={() => onNavigate("NEXT")}
          aria-label="Next"
        >
          <ChevronRightIcon fontSize="small" />
        </button>
      </div>
      <div className="dashboard-calendar-label-btn">
        <span className="dashboard-calendar-label">{getToolbarLabel(date, view)}</span>
      </div>
    </div>
    <div className="dashboard-calendar-toolbar-right">
      <button
        type="button"
        className="dashboard-calendar-pill-btn dashboard-calendar-today-btn"
        onClick={() => onNavigate("TODAY")}
      >
        Today
      </button>
      <button
        type="button"
        className="dashboard-calendar-pill-btn dashboard-calendar-view-btn"
        onClick={(event) => onOpenViewMenu(event.currentTarget)}
      >
        {VIEW_OPTIONS.find((item) => item.value === view)?.label || "Month"}
        <ExpandMoreIcon fontSize="small" />
      </button>
    </div>
  </div>
);

const normalizeRange = (range, view) => {
  if (Array.isArray(range) && range.length > 0) {
    return {
      start: range[0],
      end: range[range.length - 1],
    };
  }

  if (range && range.start && range.end) {
    return {
      start: range.start,
      end: range.end,
    };
  }

  if (range instanceof Date) {
    return {
      start: range,
      end: view === Views.DAY ? range : moment(range).endOf("month").toDate(),
    };
  }

  return null;
};

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  const hh = h % 12 === 0 ? 12 : h % 12;
  const ampm = h < 12 ? "AM" : "PM";
  const hhStr = hh < 10 ? `0${hh}` : `${hh}`;
  for (let m = 0; m < 60; m += 10) {
    const mmStr = m < 10 ? `0${m}` : `${m}`;
    TIME_OPTIONS.push(`${hhStr}:${mmStr} ${ampm}`);
  }
}

const formatTimeInputValue = (timeStr, defaultTime = "09:00") => {
  if (!timeStr) return defaultTime;
  const m = moment(timeStr, ["h:mm A", "hh:mm A", "HH:mm", "H:mm"], false);
  if (m.isValid()) {
    return m.format("HH:mm");
  }
  return defaultTime;
};

const handleNativeTimeChange = (e, setTimeFn) => {
  const val = e.target.value;
  if (!val) return;
  const m = moment(val, "HH:mm");
  if (m.isValid()) {
    setTimeFn(m.format("hh:mm A"));
  }
};

const parseFlexibleTimeMoment = (dateStr, timeStr) => {
  if (!timeStr) return moment(dateStr, "YYYY-MM-DD");
  const formats = [
    "YYYY-MM-DD h:mm A",
    "YYYY-MM-DD hh:mm A",
    "YYYY-MM-DD h:mma",
    "YYYY-MM-DD hh:mma",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD H:mm",
  ];
  return moment(`${dateStr} ${timeStr}`, formats);
};

const LEAVE_TYPE_OPTIONS = ["Loss Of Pay", "Sick Leave", "Paid Leave"];

const DashboardCalendar = ({
  events = [],
  isAdmin = false,
  loading = false,
  onApplyLeave,
  onRangeChange,
  onSaveQuickLeave,
  onUpdateQuickEvent,
  onDeleteQuickEvent,
  onOpenEditItem,
  onUpdateLeaveStatus,
  allUsers = [],
}) => {
  const [currentView, setCurrentView] = useState(Views.MONTH);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [morePopover, setMorePopover] = useState({
    anchorEl: null,
    anchorPosition: null,
    dateKey: "",
  });
  const [addTab, setAddTab] = useState("title");
  const [eventDate, setEventDate] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [eventImageUrl, setEventImageUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [addPopover, setAddPopover] = useState(createEmptyAddPopoverState);
  const [addEventTitle, setAddEventTitle] = useState("");
  const [addEventDescription, setAddEventDescription] = useState("");
  const [leaveType, setLeaveType] = useState("Loss Of Pay");
  const [leaveStartDate, setLeaveStartDate] = useState("");
  const [leaveEndDate, setLeaveEndDate] = useState("");
  const [firstLeaveHalf, setFirstLeaveHalf] = useState("first-half");
  const [secondLeaveHalf, setSecondLeaveHalf] = useState("second-half");
  const [leaveFormErrors, setLeaveFormErrors] = useState({});
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState("09:00 AM");
  const [endTime, setEndTime] = useState("10:00 AM");
  const [showToAll, setShowToAll] = useState(true);
  const [targetUsers, setTargetUsers] = useState([]);
  const [userPopoverAnchorEl, setUserPopoverAnchorEl] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [viewMenuAnchorPosition, setViewMenuAnchorPosition] = useState(null);
  const [eventDetailsPopover, setEventDetailsPopover] = useState(createEmptyEventDetailsState());
  const [calendarColors, setCalendarColors] = useState(getCalendarColorSettings());

  useEffect(() => {
    const handleColorsUpdate = () => {
      setCalendarColors(getCalendarColorSettings());
    };
    window.addEventListener(CALENDAR_COLORS_STORAGE_EVENT, handleColorsUpdate);
    fetchAndSyncCalendarColors().then((synced) => {
      if (synced) {
        setCalendarColors(synced);
      }
    });
    return () => {
      window.removeEventListener(CALENDAR_COLORS_STORAGE_EVENT, handleColorsUpdate);
    };
  }, []);

  const handleFileUpload = async (file) => {
    if (!file) return;
    const postData = new FormData();
    postData.append('file', file);
    try {
      let updaImg = await AuthApi.uploadEventImg(postData);
      if (updaImg && updaImg.status === true && updaImg.data) {
        setEventImageUrl(updaImg.data.image_url || updaImg.data.url || null);
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          setEventImageUrl(reader.result);
        };
        reader.readAsDataURL(file);
      }
    } catch (err) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEventImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadSingleFile = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const removeImg = () => {
    setEventImageUrl(null);
  };

  const calendarEvents = useMemo(
    () => {
      const normalized = events
        .map((event, index) => normalizeEventDates(event, index))
        .filter(Boolean);

      const deduped = [];
      const seenEventKeys = new Set();

      normalized.forEach((event) => {
        const comparableKey = getComparableCalendarEventKey(event.resource || event);
        if (seenEventKeys.has(comparableKey)) {
          return;
        }

        seenEventKeys.add(comparableKey);
        deduped.push(event);
      });

      deduped.sort((a, b) => {
        const startA = a.start.getTime();
        const startB = b.start.getTime();
        if (startA !== startB) {
          return startA - startB;
        }

        const endA = a.end.getTime();
        const endB = b.end.getTime();
        if (endA !== endB) {
          return endB - endA; // longer events first
        }

        const allDayA = Boolean(a.allDay);
        const allDayB = Boolean(b.allDay);
        if (allDayA !== allDayB) {
          return allDayA ? -1 : 1; // allDay first
        }

        return (a.title || "").localeCompare(b.title || "");
      });

      return deduped;
    },
    [events]
  );
  const eventsByDay = useMemo(() => {
    return calendarEvents.reduce((accumulator, event) => {
      if (!event || !event.start) {
        return accumulator;
      }

      const startMoment = moment(event.start).startOf("day");
      const endMoment = moment(event.end || event.start).startOf("day");

      const current = startMoment.clone();
      while (current.isSameOrBefore(endMoment, "day")) {
        const dateKey = current.format("YYYY-MM-DD");
        if (!accumulator[dateKey]) {
          accumulator[dateKey] = [];
        }

        if (!accumulator[dateKey].some((item) => item.id === event.id)) {
          accumulator[dateKey].push(event);
        }

        current.add(1, "day");
      }

      return accumulator;
    }, {});
  }, [calendarEvents]);

  const monthCalendarEvents = useMemo(() => {
    return Object.keys(eventsByDay)
      .map((dateKey) => {
        const dayEvents = eventsByDay[dateKey];
        if (!Array.isArray(dayEvents) || dayEvents.length === 0) {
          return null;
        }

        const firstEvent = dayEvents[0];
        const dayMoment = moment(dateKey, "YYYY-MM-DD");

        return {
          ...firstEvent,
          id: `month_${dateKey}_${firstEvent.id}`,
          start: dayMoment.clone().startOf("day").toDate(),
          end: dayMoment.clone().startOf("day").add(23, "hours").add(59, "minutes").toDate(),
          allDay: true,
          originalEvent: firstEvent,
          dateKey: dateKey,
        };
      })
      .filter(Boolean);
  }, [eventsByDay]);

  const displayedCalendarEvents = currentView === Views.MONTH ? monthCalendarEvents : calendarEvents;
  const popoverEvents = morePopover.dateKey ? (eventsByDay[morePopover.dateKey] || []) : [];

  const handleRangeChange = (range, view = currentView) => {
    if (typeof onRangeChange !== "function") {
      return;
    }

    const normalized = normalizeRange(range, view);
    if (normalized) {
      onRangeChange(normalized.start, normalized.end);
    }
  };

  const handleNavigate = (date) => {
    setCurrentDate(date);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleOpenViewMenu = (anchorEl) => {
    if (!anchorEl) {
      return;
    }

    const triggerRect = anchorEl.getBoundingClientRect();
    setViewMenuAnchorPosition({
      top: Math.round(triggerRect.bottom + 6),
      left: Math.round(triggerRect.left),
    });
  };

  const handleCloseViewMenu = () => {
    setViewMenuAnchorPosition(null);
  };

  const handleSelectView = (view) => {
    setCurrentView(view);
    setViewMenuAnchorPosition(null);
  };

  const handleSelectSlot = (slotInfo) => {
    const { start, end, box, req } = slotInfo;

    const clientX = box ? box.clientX : (req ? req.clientX : (window.event ? window.event.clientX : null));
    const clientY = box ? box.clientY : (req ? req.clientY : (window.event ? window.event.clientY : null));

    const computedTop = clientY ? Math.max(150, Math.min(clientY + 5, window.innerHeight - 520)) : 200;
    const computedLeft = clientX ? Math.max(20, Math.min(clientX + 5, window.innerWidth - 650)) : 400;

    setAddPopover({
      anchorPosition: { top: computedTop, left: computedLeft },
      start,
      end,
      formattedDate: formatPopoverDateText(start, end),
      mode: "create",
      eventId: null,
    });
    setAddTab("title");
    setAddEventTitle("");
    setAddEventDescription("");
    setEventDescription("");
    setEventDate(start ? moment(start).format("YYYY-MM-DD") : "");
    setEventImageUrl(null);
    setEventDetailsPopover(createEmptyEventDetailsState());
  };

  const handleLeaveStartDateChange = (val) => {
    setLeaveStartDate(val);
    const errors = { ...leaveFormErrors };

    if (!val) {
      errors.startDate = "Cannot be blank";
    } else {
      delete errors.startDate;
    }

    const currentEnd = leaveEndDate || val;
    if (currentEnd && moment(currentEnd).isBefore(moment(val), "day")) {
      errors.endDate = "End date cannot be before start date";
      swal("Invalid Date", "End date cannot be before start date", "error");
    } else {
      delete errors.endDate;
    }

    setLeaveFormErrors(errors);
  };

  const handleLeaveEndDateChange = (val) => {
    setLeaveEndDate(val);
    const errors = { ...leaveFormErrors };

    if (!val) {
      errors.endDate = "Cannot be blank";
    } else {
      const currentStart = leaveStartDate || eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "");
      if (currentStart && moment(val).isBefore(moment(currentStart), "day")) {
        errors.endDate = "End date cannot be before start date";
        swal("Invalid Date", "End date cannot be before start date", "error");
      } else {
        delete errors.endDate;
      }
    }

    setLeaveFormErrors(errors);
  };

  const handleCloseAddPopover = () => {
    setAddPopover(createEmptyAddPopoverState());
    setAddTab("title");
    setAddEventTitle("");
    setAddEventDescription("");
    setEventDescription("");
    setAllDay(true);
    setStartTime("07:30");
    setEndTime("18:00");
    setShowToAll(true);
    setEventDate("");
    setLeaveStartDate("");
    setLeaveEndDate("");
    setLeaveFormErrors({});
    setEventImageUrl(null);
  };

  const handleMoreDetails = () => {
    if (typeof onApplyLeave === "function" && addPopover.start) {
      onApplyLeave(addPopover.start, addPopover.end, addEventTitle);
    }
    handleCloseAddPopover();
    setTargetUsers([]);
  };

  const handleSaveQuickEvent = async () => {
    let titleToSave = addEventTitle.trim();

    if (addTab === "event") {
      const titleToSave = addEventTitle.trim();
      const dateToSave = eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "");
      const descToSave = eventDescription.trim();

      if (!titleToSave) {
        swal("Warning", "Please enter Event Name!", "warning");
        return;
      }
      if (!dateToSave) {
        swal("Warning", "Please select Event Date!", "warning");
        return;
      }

      swal({
        title: addPopover.mode === "edit" ? "Updating Event..." : "Saving Event...",
        text: "Please wait...",
        buttons: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
      });

      try {
        let res1;
        if (addPopover.mode === "edit" && addPopover.eventId) {
          res1 = await AuthApi.updateUpcomingEvent(
            {
              upcomingEventName: titleToSave,
              upcomingEventdate: dateToSave,
              description: descToSave || titleToSave,
              image: eventImageUrl || null
            },
            addPopover.eventId
          );
        } else {
          res1 = await AuthApi.createUpcomingEvent({
            upcomingEventName: titleToSave,
            upcomingEventdate: dateToSave,
            description: descToSave || titleToSave,
            image: eventImageUrl || null
          });

          await AuthApi.createEvent(
            { eventName: titleToSave, eventdate: dateToSave },
            eventImageUrl ? [eventImageUrl] : []
          );
        }

        swal.close();

        if ((res1 && res1.status === true) || res1) {
          swal("Success", addPopover.mode === "edit" ? "Event updated successfully!" : "Event added successfully!", "success");
          handleCloseAddPopover();
          if (typeof onRangeChange === "function") {
            onRangeChange();
          }
        } else {
          swal("Error", (res1 && res1.message) ? res1.message : "Something went wrong, Please try later!", "error");
        }
      } catch (err) {
        swal.close();
        swal("Error", "Something went wrong, Please try later!", "error");
      }
      return;
    }

    if (addTab === "holiday") {
      const titleToSave = addEventTitle.trim();
      const dateToSave = eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "");

      if (!titleToSave) {
        swal("Warning", "Please enter Holiday Name!", "warning");
        return;
      }
      if (!dateToSave) {
        swal("Warning", "Please select Holiday Date!", "warning");
        return;
      }

      swal({
        title: addPopover.mode === "edit" ? "Updating Holiday..." : "Saving Holiday...",
        text: "Please wait...",
        buttons: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
      });

      try {
        let res1;
        const formattedDate = moment(dateToSave, "YYYY-MM-DD").format("DD-MM-YYYY");

        if (addPopover.mode === "edit" && addPopover.eventId) {
          res1 = await AuthApi.updateLeave(
            { leaveName: titleToSave, date: formattedDate, leave_id: addPopover.eventId },
            addPopover.eventId
          );
        } else {
          res1 = await AuthApi.createHoliday({
            leaveName: titleToSave,
            date: formattedDate,
          });
        }

        swal.close();

        if ((res1 && res1.status === true) || res1) {
          swal("Success", addPopover.mode === "edit" ? "Holiday updated successfully!" : "Holiday added successfully!", "success");
          handleCloseAddPopover();
          if (typeof onRangeChange === "function") {
            onRangeChange();
          }
        } else {
          swal("Error", (res1 && res1.message) ? res1.message : "Something went wrong, Please try later!", "error");
        }
      } catch (err) {
        swal.close();
        swal("Error", "Something went wrong, Please try later!", "error");
      }
      return;
    }

    if (addTab === "timeEntry") {
      const dateToSave = eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "");
      const sTime = startTime || "07:30";
      const eTime = endTime || "18:00";
      const reasonToSave = addEventDescription.trim();

      if (!dateToSave) {
        swal("Warning", "Please select Date!", "warning");
        return;
      }
      if (!reasonToSave) {
        swal("Warning", "Please enter Reason!", "warning");
        return;
      }

      swal({
        title: "Saving Time Entry...",
        text: "Please wait...",
        buttons: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
      });

      try {
        const userObj = ls("user") || {};
        const userId = userObj.id || userObj.Id;

        const res1 = await AuthApi.addTime({
          id: userId,
          selectedDate: dateToSave,
          startTime: sTime,
          endTime: eTime,
          reason: reasonToSave,
        });

        swal.close();

        if (res1 && (res1.status === true || res1 === true)) {
          swal("Success", "Time entry added successfully", "success");
          handleCloseAddPopover();
          if (typeof onRangeChange === "function") {
            onRangeChange();
          }
        } else {
          swal("Error", (typeof res1 === "string" ? res1 : (res1 && res1.message) ? res1.message : "Something went wrong, Please try later!"), "error");
        }
      } catch (err) {
        swal.close();
        swal("Error", "Something went wrong, Please try later!", "error");
      }
      return;
    }

    if (addTab === "leave") {
      const sDate = leaveStartDate || eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "");
      const eDate = leaveEndDate || sDate;
      const desc = addEventDescription.trim();

      const errors = {};
      if (!sDate) {
        errors.startDate = "Cannot be blank";
      }
      if (!eDate) {
        errors.endDate = "Cannot be blank";
      } else if (sDate && moment(eDate).isBefore(moment(sDate), "day")) {
        errors.endDate = "End date cannot be before start date";
        swal("Invalid Date", "End date cannot be before start date", "error");
        setLeaveFormErrors(errors);
        return;
      }
      if (!desc) {
        errors.description = "Cannot be blank";
      }

      if (Object.keys(errors).length > 0) {
        setLeaveFormErrors(errors);
        swal("Warning", errors.endDate || errors.startDate || errors.description || "Please fill all required fields!", "warning");
        return;
      }

      if (addPopover.start) {
        addPopover.start = moment(sDate, "YYYY-MM-DD").toDate();
        addPopover.end = moment(eDate, "YYYY-MM-DD").toDate();
      }
    }

    titleToSave = addEventTitle.trim() || (addTab === "leave" ? leaveType : titleToSave);

    if (addTab === "meeting" && titleToSave) {
      const cleanTitle = titleToSave.replace(/^(title|reminder|calendar)\s*[-:]\s*/i, "").trim();
      if (!/^meeting\s*[-:]/i.test(cleanTitle)) {
        titleToSave = `Meeting - ${cleanTitle}`;
      } else {
        titleToSave = cleanTitle;
      }
    }

    if (!titleToSave && addTab !== "leave") {
      swal("Warning", "Please enter a title!", "warning");
      return;
    }

    if (typeof onSaveQuickLeave === "function" && addPopover.start) {
      let finalStart = addPopover.start;
      let finalEnd = addPopover.end;

      if (!allDay) {
        const dateStr = moment(addPopover.start).format("YYYY-MM-DD");
        const startDateTime = parseFlexibleTimeMoment(dateStr, startTime);

        const startMoment = moment(addPopover.start);
        const rawEndMoment = addPopover.end ? moment(addPopover.end) : startMoment;
        const hasSingleDaySelection = !addPopover.end || rawEndMoment.diff(startMoment, "days") <= 1;

        const endDateStr = hasSingleDaySelection
          ? dateStr
          : rawEndMoment.clone().subtract(1, "day").format("YYYY-MM-DD");

        const endDateTime = parseFlexibleTimeMoment(endDateStr, endTime);

        finalStart = startDateTime.toDate();
        finalEnd = endDateTime.toDate();
      }

      const descriptionToSave = addEventDescription.trim();

      const targetUserIds = targetUsers ? targetUsers.map(u => u.value) : [];
      const computedShowToAll = (isAdmin && allUsers && allUsers.length > 0 && targetUsers.length === allUsers.length) ? true : false;

      const extraLeaveData = {
        leaveType: leaveType,
        firstleavetype: firstLeaveHalf,
        secondleavetype: secondLeaveHalf,
        leaveStartDate: leaveStartDate || eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : ""),
        leaveEndDate: leaveEndDate || leaveStartDate || eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : ""),
      };

      if (addPopover.mode === "edit" && typeof onUpdateQuickEvent === "function" && addPopover.eventId) {
        const didUpdate = await onUpdateQuickEvent(
          addPopover.eventId,
          finalStart,
          finalEnd,
          titleToSave,
          descriptionToSave,
          allDay,
          computedShowToAll,
          addPopover.eventMeta || null,
          targetUserIds,
          addTab,
          extraLeaveData
        );
        if (didUpdate) {
          handleCloseAddPopover();
          setTargetUsers([]);
        }
      } else if (typeof onSaveQuickLeave === "function") {
        const didSave = await onSaveQuickLeave(
          finalStart,
          finalEnd,
          titleToSave,
          descriptionToSave,
          allDay,
          computedShowToAll,
          targetUserIds,
          addTab,
          extraLeaveData
        );
        if (didSave) {
          handleCloseAddPopover();
          setTargetUsers([]);
        }
      }
    }
  };
  const handleOpenMorePopover = (nativeEvent, dateKey) => {
    nativeEvent.preventDefault();
    nativeEvent.stopPropagation();
    const triggerRect = nativeEvent.currentTarget.getBoundingClientRect();

    setMorePopover({
      anchorEl: nativeEvent.currentTarget,
      anchorPosition: {
        top: triggerRect.bottom + 2,
        left: triggerRect.left,
      },
      dateKey,
    });
  };
  const handleCloseMorePopover = () => {
    setMorePopover({
      anchorEl: null,
      anchorPosition: null,
      dateKey: "",
    });
  };

  const handleSelectEventFromMorePopover = (selectedEvent, nativeEvent) => {
    handleCloseMorePopover();
    handleSelectEvent(selectedEvent, nativeEvent);
  };

  const handleSelectEvent = (selectedEvent, nativeEvent) => {
    const triggerRect = nativeEvent && nativeEvent.currentTarget
      ? nativeEvent.currentTarget.getBoundingClientRect()
      : null;

    setEventDetailsPopover({
      anchorPosition: triggerRect
        ? { top: Math.round(triggerRect.bottom + 8), left: Math.round(triggerRect.left) }
        : { top: 220, left: 420 },
      event: selectedEvent,
    });
    setViewMenuAnchorPosition(null);
    setMorePopover((prev) => ({ ...prev, anchorPosition: null, dateKey: "" }));
  };

  const handleCloseEventDetailsPopover = () => {
    setEventDetailsPopover(createEmptyEventDetailsState());
  };

  const handleEditSelectedEvent = () => {
    const selectedEvent = eventDetailsPopover.event;
    if (!selectedEvent) {
      return;
    }

    const rawEvent = selectedEvent.resource || {};
    const updateMeta = getEventUpdateMeta(rawEvent, selectedEvent.category);
    const resolvedEventId = getEventIdentifier(rawEvent);
    const category = String(selectedEvent.category || rawEvent.Category || rawEvent.category || "").toLowerCase();
    const strategy = updateMeta ? updateMeta.strategy : category;

    if (strategy === "event" || strategy === "upcoming" || category === "event" || category === "upcoming") {
      const detailsAnchor = eventDetailsPopover.anchorPosition;
      handleCloseEventDetailsPopover();

      const eventName = rawEvent.upcomingEventName || rawEvent.upcoming_event_name || rawEvent.eventName || rawEvent.name || rawEvent.title || selectedEvent.title || "";
      const eventDateVal = rawEvent.upcomingEventdate || rawEvent.upcoming_event_date || rawEvent.eventdate || rawEvent.date || (selectedEvent.start ? moment(selectedEvent.start).format("YYYY-MM-DD") : "");
      const eventDesc = rawEvent.description || rawEvent.Description || selectedEvent.description || "";
      const eventImg = rawEvent.image || rawEvent.image_url || rawEvent.img || null;

      setAddPopover({
        anchorPosition: detailsAnchor || { top: 200, left: 400 },
        start: selectedEvent.start,
        end: selectedEvent.end,
        formattedDate: moment(selectedEvent.start).format("YYYY-MM-DD"),
        mode: "edit",
        eventId: resolvedEventId || (updateMeta ? updateMeta.id : null),
        eventMeta: updateMeta,
      });
      setAddTab("event");
      setAddEventTitle(eventName);
      setEventDate(eventDateVal);
      setEventDescription(eventDesc);
      setEventImageUrl(eventImg);
      return;
    }

    if (strategy === "holiday" || category === "holiday") {
      const detailsAnchor = eventDetailsPopover.anchorPosition;
      handleCloseEventDetailsPopover();

      const holidayName = rawEvent.leaveName || rawEvent.leave_name || rawEvent.name || rawEvent.title || selectedEvent.title || "";
      const rawDate = rawEvent.date || rawEvent.holidayDate || (selectedEvent.start ? moment(selectedEvent.start).format("YYYY-MM-DD") : "");
      const holidayDateVal = moment(rawDate, ["YYYY-MM-DD", "DD-MM-YYYY"]).isValid()
        ? moment(rawDate, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("YYYY-MM-DD")
        : (selectedEvent.start ? moment(selectedEvent.start).format("YYYY-MM-DD") : "");

      setAddPopover({
        anchorPosition: detailsAnchor || { top: 200, left: 400 },
        start: selectedEvent.start,
        end: selectedEvent.end,
        formattedDate: moment(selectedEvent.start).format("YYYY-MM-DD"),
        mode: "edit",
        eventId: resolvedEventId || (updateMeta ? updateMeta.id : null),
        eventMeta: updateMeta,
      });
      setAddTab("holiday");
      setAddEventTitle(holidayName);
      setEventDate(holidayDateVal);
      return;
    }

    if (strategy === "leave" || category === "leave") {
      const detailsAnchor = eventDetailsPopover.anchorPosition;
      handleCloseEventDetailsPopover();

      const leaveTitleVal = rawEvent.leaveName || rawEvent.leave_name || rawEvent.name || rawEvent.title || selectedEvent.title || "Leave";
      const rawDate = rawEvent.date || rawEvent.startDate || (selectedEvent.start ? moment(selectedEvent.start).format("YYYY-MM-DD") : "");
      const rawEndDate = rawEvent.endDate || rawEvent.end_date || rawDate;
      const leaveDateVal = moment(rawDate, ["YYYY-MM-DD", "DD-MM-YYYY"]).isValid()
        ? moment(rawDate, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("YYYY-MM-DD")
        : (selectedEvent.start ? moment(selectedEvent.start).format("YYYY-MM-DD") : "");
      const leaveEndDateVal = moment(rawEndDate, ["YYYY-MM-DD", "DD-MM-YYYY"]).isValid()
        ? moment(rawEndDate, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("YYYY-MM-DD")
        : leaveDateVal;
      const leaveDesc = rawEvent.description || rawEvent.Description || rawEvent.reason || selectedEvent.description || "";
      const leaveTypeVal = rawEvent.leaveType || rawEvent.leave_type || "Loss Of Pay";
      const fHalf = rawEvent.firstleavetype || rawEvent.first_leave_type || "first-half";
      const sHalf = rawEvent.secondleavetype || rawEvent.second_leave_type || "second-half";

      setAddPopover({
        anchorPosition: detailsAnchor || { top: 200, left: 400 },
        start: selectedEvent.start,
        end: selectedEvent.end,
        formattedDate: moment(selectedEvent.start).format("YYYY-MM-DD"),
        mode: "edit",
        eventId: resolvedEventId || (updateMeta ? updateMeta.id : null),
        eventMeta: updateMeta,
      });
      setAddTab("leave");
      setAddEventTitle(leaveTitleVal);
      setLeaveType(leaveTypeVal);
      setLeaveStartDate(leaveDateVal);
      setLeaveEndDate(leaveEndDateVal);
      setFirstLeaveHalf(fHalf);
      setSecondLeaveHalf(sHalf);
      setAddEventDescription(leaveDesc);
      return;
    }

    if (
      updateMeta &&
      updateMeta.strategy &&
      updateMeta.strategy !== "calendar" &&
      typeof onOpenEditItem === "function" &&
      resolvedEventId
    ) {
      handleCloseEventDetailsPopover();
      onOpenEditItem(resolvedEventId, {
        ...updateMeta,
        rawEvent,
        category: selectedEvent.category || rawEvent.Category || rawEvent.category || "",
        updateStrategy: updateMeta.strategy,
      });
      return;
    }

    const start = selectedEvent.start;
    const end = selectedEvent.end;
    const startMoment = moment(start);
    const endMoment = moment(end);
    const rawShowToAll = rawEvent.showToAll ?? rawEvent.show_to_all ?? rawEvent.ShowToAll ?? rawEvent.show_all;
    const rawAllDay = rawEvent.IsAllDay ?? rawEvent.isAllDay ?? rawEvent.is_all_day ?? selectedEvent.allDay;
    const isAllDayValue = getBooleanFlag(rawAllDay, Boolean(selectedEvent.allDay));
    const rawTargetUsers = rawEvent.target_user_ids || rawEvent.TargetUserIds || rawEvent.targetUserIds || [];
    let initialTargetUsers = [];
    if (allUsers && allUsers.length > 0) {
      let parsedIds = [];
      if (Array.isArray(rawTargetUsers)) {
        parsedIds = rawTargetUsers.map(id => String(id));
      } else if (typeof rawTargetUsers === 'string') {
        try {
          parsedIds = JSON.parse(rawTargetUsers).map(id => String(id));
        } catch (e) {
          parsedIds = rawTargetUsers.split(',').map(id => id.trim()).filter(Boolean);
        }
      }

      initialTargetUsers = allUsers
        .filter(u => parsedIds.includes(String(u.id)))
        .map(u => ({ 
          value: u.id, 
          label: (u.firstName ? `${u.firstName} ${u.lastName || ''}` : (u.name || u.email || `User ${u.id}`)).trim() 
        }));
    }

    const rawTitleStr = String(getEventTitleValue(rawEvent) || selectedEvent.title || "").toLowerCase();
    const editCat = String(selectedEvent.category || rawEvent.Category || rawEvent.category || rawEvent.categoryName || updateMeta?.category || "").trim().toLowerCase();

    let resolvedTab = "title";
    if (editCat === "meeting" || rawTitleStr.includes("meeting") || rawTitleStr.includes("scrum")) {
      resolvedTab = "meeting";
    } else if (editCat === "event" || editCat === "upcoming") {
      resolvedTab = "event";
    } else if (editCat === "holiday") {
      resolvedTab = "holiday";
    } else if (editCat === "leave") {
      resolvedTab = "leave";
    } else if (editCat === "timeentry" || editCat === "time") {
      resolvedTab = "timeEntry";
    } else {
      resolvedTab = isAdmin ? "title" : "leave";
    }

    setAddPopover({
      anchorPosition: eventDetailsPopover.anchorPosition || { top: 220, left: 420 },
      start,
      end,
      formattedDate: formatPopoverDateText(start, end),
      mode: "edit",
      eventId: resolvedEventId,
      eventMeta: {
        ...updateMeta,
        rawEvent,
        category: resolvedTab,
        updateStrategy: updateMeta?.strategy || null,
      },
    });
    setAddTab(resolvedTab);
    setAddEventTitle(getEventTitleValue(rawEvent) || selectedEvent.title || "");
    setAddEventDescription(rawEvent.description || rawEvent.Description || rawEvent.details || rawEvent.message || "");
    setAllDay(isAllDayValue);
    setShowToAll(getBooleanFlag(rawShowToAll, true));
    setTargetUsers(initialTargetUsers);
    setStartTime(startMoment.isValid() ? startMoment.format("hh:mm A") : "09:00 AM");
    setEndTime(endMoment.isValid() ? endMoment.format("hh:mm A") : "10:00 AM");
    handleCloseEventDetailsPopover();
  };

  const handleDeleteSelectedEvent = async () => {
    const selectedEvent = eventDetailsPopover.event;
    if (!selectedEvent) {
      return;
    }

    const rawEvent = selectedEvent.resource || {};
    const deleteMeta = getEventDeleteMeta(rawEvent, selectedEvent.category);
    if (!deleteMeta || !deleteMeta.id || !isDeleteSupportedForMeta(deleteMeta) || typeof onDeleteQuickEvent !== "function") {
      swal("Error", "This event cannot be deleted right now.", "error");
      return;
    }

    handleCloseEventDetailsPopover();
    onDeleteQuickEvent(
      deleteMeta.id,
      getEventTitleValue(rawEvent) || selectedEvent.title || "this event",
      {
        category: selectedEvent.category || rawEvent.Category || rawEvent.category || "",
        rawEvent,
        deleteStrategy: deleteMeta.strategy,
      }
    );
  };

  const eventPropGetter = (event) => ({
    className: `dashboard-calendar-rbc-event dashboard-calendar-rbc-event-${event.category}`,
    style: {
      backgroundColor: "transparent",
      border: "none",
      padding: 0,
      boxShadow: "none",
    },
  });

  const dayPropGetter = (date) => {
    const isToday = moment(date).isSame(new Date(), "day");
    return {
      className: isToday ? "dashboard-calendar-day-today" : "",
    };
  };
  const isSelectedEventReadonly = Boolean(
    eventDetailsPopover.event?.resource?.Readonly ??
    eventDetailsPopover.event?.resource?.readonly ??
    false
  );

  const selectedEventCategory = String(
    eventDetailsPopover.event?.category ||
    eventDetailsPopover.event?.resource?.Category ||
    ""
  ).toLowerCase();

  const selectedEventTitleLower = String(
    eventDetailsPopover.event?.title ||
    getEventTitleValue(eventDetailsPopover.event?.resource) ||
    ""
  ).toLowerCase();

  const isSelectedEventLeave = selectedEventCategory === "leave";
  const isSelectedEventBirthday =
    selectedEventCategory === "birthday" ||
    selectedEventCategory === "bday" ||
    selectedEventTitleLower.startsWith("birthday") ||
    selectedEventTitleLower.includes("birthday") ||
    selectedEventTitleLower.includes("bday");

  const canEditSelectedEvent = Boolean(
    eventDetailsPopover.event &&
    !isSelectedEventLeave &&
    !isSelectedEventBirthday &&
    (!isSelectedEventReadonly || isAdmin) &&
    getEventUpdateMeta(
      eventDetailsPopover.event.resource || {},
      eventDetailsPopover.event.category
    )
  );

  const canDeleteSelectedEvent = Boolean(
    eventDetailsPopover.event &&
    !isSelectedEventLeave &&
    !isSelectedEventBirthday &&
    (!isSelectedEventReadonly || isAdmin) &&
    isDeleteSupportedForMeta(
      getEventDeleteMeta(
        eventDetailsPopover.event.resource || {},
        eventDetailsPopover.event.category
      )
    )
  );

  const hexToRgba = (hex, alpha = 0.18) => {
    if (!hex || typeof hex !== "string") return null;
    let c = hex.replace("#", "");
    if (c.length === 3) {
      c = c.split("").map((x) => x + x).join("");
    }
    if (c.length !== 6) return null;
    const num = parseInt(c, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getEventCategoryKey = (evt) => {
    const evtCat = String(evt?.category || evt?.resource?.Category || evt?.resource?.category || "").toLowerCase();
    const titleLower = String(evt?.title || getEventTitleValue(evt?.resource) || "").toLowerCase();

    if (evtCat === "birthday" || evtCat === "bday" || titleLower.includes("birthday") || titleLower.includes("bday")) {
      return "Birthday";
    }

    if (evtCat === "holiday" || titleLower.includes("holiday")) {
      return "Holiday";
    }

    if (evtCat === "meeting" || titleLower.includes("meeting")) {
      return "Meeting";
    }

    if (
      evtCat === "leave" ||
      titleLower.includes("leave") ||
      titleLower.includes("sick leave") ||
      titleLower.includes("casual leave") ||
      titleLower.includes("loss of pay")
    ) {
      const rawStatus = String(
        evt?.resource?.LeaveStatus ||
        evt?.resource?.Status ||
        evt?.resource?.status ||
        evt?.leaveStatus ||
        evt?.status ||
        ""
      ).toLowerCase();

      if (rawStatus.includes("pending") || titleLower.includes("pending")) {
        return "Leave - Pending";
      }
      if (rawStatus.includes("declin") || titleLower.includes("declin")) {
        return "Leave - Declined";
      }
      if (rawStatus.includes("unapprov") || titleLower.includes("unapprov")) {
        return "Leave - Unapproved";
      }
      if (rawStatus.includes("cancel") || titleLower.includes("cancel")) {
        return "Leave - Cancelled";
      }
      return "Leave - Approved";
    }

    if (evtCat === "title" || (titleLower.startsWith("title - ") && !titleLower.includes("leave"))) {
      return "Title";
    }

    if (evtCat === "event") {
      return "Event";
    }

    return "Event";
  };

  const getEventPillStyle = (evt) => {
    const catKey = getEventCategoryKey(evt);
    const customHex = calendarColors?.[catKey];

    if (!customHex) {
      return { pillStyle: {}, dotStyle: {} };
    }

    const bgRgba = hexToRgba(customHex, 0.18) || customHex;
    return {
      pillStyle: {
        backgroundColor: bgRgba,
        color: customHex,
        borderLeft: `3px solid ${customHex}`,
      },
      dotStyle: {
        backgroundColor: customHex,
      },
    };
  };

  const getLeaveCategoryClass = (evt) => {
    const evtCat = String(evt?.category || evt?.resource?.Category || evt?.resource?.category || "").toLowerCase();
    const titleLower = String(evt?.title || getEventTitleValue(evt?.resource) || "").toLowerCase();
    if (evtCat === "meeting" || titleLower.includes("meeting")) {
      return "meeting";
    }

    const isLeave =
      evtCat === "leave" ||
      titleLower.includes("leave") ||
      titleLower.includes("sick leave") ||
      titleLower.includes("casual leave") ||
      titleLower.includes("loss of pay");

    if (!evt || !isLeave) {
      return evtCat || "event";
    }

    const rawStatus = String(
      evt.resource?.LeaveStatus ||
      evt.resource?.Status ||
      evt.resource?.status ||
      evt.leaveStatus ||
      evt.status ||
      ""
    ).toLowerCase();

    if (rawStatus.includes("pending") || titleLower.includes("pending")) {
      return "leave-pending";
    }
    if (rawStatus.includes("declin") || titleLower.includes("declin")) {
      return "leave-declined";
    }
    if (rawStatus.includes("unapprov") || titleLower.includes("unapprov")) {
      return "leave-unapproved";
    }
    if (rawStatus.includes("cancel") || titleLower.includes("cancel")) {
      return "leave-cancelled";
    }
    return "leave-approved";
  };

  const EventRenderer = ({ event }) => {
    const dateKey = event.dateKey || moment(event.start).format("YYYY-MM-DD");
    const dayEvents = eventsByDay[dateKey] || [];
    const displayEvent = event.originalEvent || dayEvents[0] || event;
    const tooltipText = formatEventTooltip(displayEvent);
    const displayTitle = formatEventDisplayTitle(displayEvent);

    const pillCategoryClass = getLeaveCategoryClass(displayEvent);
    const { pillStyle, dotStyle } = getEventPillStyle(displayEvent);

    if (currentView === Views.MONTH) {
      const hiddenCount = dayEvents.length - 1;

      return (
        <div className="dashboard-calendar-event-stack">
          <Tooltip
            title={
              <span className="dashboard-calendar-tooltip-copy">
                {tooltipText.split("\n").map((line, index) => (
                  <React.Fragment key={`${displayEvent.id}-tooltip-line-${index}`}>
                    {index > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </span>
            }
            arrow
            placement="top"
            classes={{
              tooltip: "dashboard-calendar-tooltip",
              arrow: "dashboard-calendar-tooltip-arrow",
            }}
          >
            <div
              className={`dashboard-calendar-event-pill dashboard-calendar-event-pill-${pillCategoryClass}`}
              style={pillStyle}
              onClick={(nativeEvent) => handleSelectEvent(displayEvent, nativeEvent)}
            >
              <span className="dashboard-calendar-event-dot" style={dotStyle} />
              <span className="dashboard-calendar-event-text">{displayTitle}</span>
            </div>
          </Tooltip>
          {hiddenCount > 0 && (
            <button
              type="button"
              className="dashboard-calendar-custom-more"
              onClick={(nativeEvent) => {
                nativeEvent.stopPropagation();
                handleOpenMorePopover(nativeEvent, dateKey);
              }}
            >
              +{hiddenCount} more
            </button>
          )}
        </div>
      );
    }

    return (
      <Tooltip
        title={
          <span className="dashboard-calendar-tooltip-copy">
            {tooltipText.split("\n").map((line, index) => (
              <React.Fragment key={`${event.id}-tooltip-line-${index}`}>
                {index > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </span>
        }
        arrow
        placement="top"
        classes={{
          tooltip: "dashboard-calendar-tooltip",
          arrow: "dashboard-calendar-tooltip-arrow",
        }}
      >
        <div className={`dashboard-calendar-event-pill dashboard-calendar-event-pill-${pillCategoryClass}`} style={pillStyle}>
          <span className="dashboard-calendar-event-dot" style={dotStyle} />
          <span className="dashboard-calendar-event-text">{displayTitle}</span>
        </div>
      </Tooltip>
    );
  };

  return (
    <Card className="dashboard-calendar-card">
      <CardContent className="dashboard-calendar-card-content">
        <div className="dashboard-calendar-header">
          <Typography variant="h5" className="dashboard-calendar-title">
            Team Calendar
          </Typography>
        </div>

        <div className={`dashboard-calendar-shell${loading ? " is-loading" : ""}`}>
          <div className="dashboard-calendar-rbc-wrap">
            <Calendar
              localizer={localizer}
              events={displayedCalendarEvents}
              views={[Views.DAY, Views.WEEK, Views.WORK_WEEK, Views.MONTH, Views.AGENDA]}
              view={currentView}
              date={currentDate}
              selectable
              popup={false}
              popupOffset={8}
              toolbar
              allDayMaxRows={1}
              startAccessor="start"
              endAccessor="end"
              tooltipAccessor={() => ""}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              onRangeChange={handleRangeChange}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventPropGetter}
              dayPropGetter={dayPropGetter}
              components={{
                toolbar: (toolbarProps) => (
                  <CustomToolbar
                    {...toolbarProps}
                    onOpenViewMenu={handleOpenViewMenu}
                  />
                ),
                event: EventRenderer,
              }}
              messages={{
                showMore: (total) => `+${total} more`,
              }}
              formats={{
                weekdayFormat: (date, culture, nextLocalizer) => nextLocalizer.format(date, "dddd", culture),
                dayFormat: (date, culture, nextLocalizer) => nextLocalizer.format(date, "D", culture),
                agendaDateFormat: (date, culture, nextLocalizer) => nextLocalizer.format(date, "DD MMM YYYY", culture),
              }}
            />
          </div>

          {loading && (
            <div className="dashboard-calendar-loading">
              <Typography variant="body1">Loading calendar...</Typography>
            </div>
          )}
        </div>

        <Popover
          open={Boolean(viewMenuAnchorPosition)}
          anchorReference="anchorPosition"
          anchorPosition={viewMenuAnchorPosition || { top: 0, left: 0 }}
          onClose={handleCloseViewMenu}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          disableScrollLock
          classes={{ paper: "dashboard-calendar-toolbar-popover" }}
        >
          <div className="dashboard-calendar-toolbar-menu">
            {VIEW_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`dashboard-calendar-toolbar-menu-item${currentView === option.value ? " is-active" : ""}`}
                onClick={() => handleSelectView(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Popover>

        <Popover
          open={Boolean(morePopover.anchorPosition)}
          disableScrollLock
          anchorReference="anchorPosition"
          anchorPosition={morePopover.anchorPosition || { top: 0, left: 0 }}
          onClose={handleCloseMorePopover}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          classes={{ paper: "dashboard-calendar-more-popover" }}
        >
          <div className="dashboard-calendar-more-popover-header">
            <div>
              <Typography variant="subtitle2" className="dashboard-calendar-more-popover-day">
                {morePopover.dateKey ? moment(morePopover.dateKey).format("ddd") : ""}
              </Typography>
              <Typography variant="h6" className="dashboard-calendar-more-popover-date">
                {morePopover.dateKey ? moment(morePopover.dateKey).format("DD") : ""}
              </Typography>
            </div>
            <IconButton size="small" onClick={handleCloseMorePopover} className="dashboard-calendar-more-popover-close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={`dashboard-calendar-more-popover-body${popoverEvents.length > 4 ? " is-scrollable" : ""}`}>
            {popoverEvents.map((popoverEvent) => {
              const itemCategoryClass = getLeaveCategoryClass(popoverEvent);
              const { pillStyle } = getEventPillStyle(popoverEvent);

              return (
                <button
                  key={popoverEvent.id}
                  type="button"
                  className={`dashboard-calendar-more-item dashboard-calendar-more-item-${itemCategoryClass}`}
                  style={pillStyle}
                  onClick={(nativeEvent) => handleSelectEventFromMorePopover(popoverEvent, nativeEvent)}
                >
                  <span className="dashboard-calendar-event-text">{formatEventDisplayTitle(popoverEvent)}</span>
                </button>
              );
            })}
          </div>
        </Popover>

        <Popover
          open={Boolean(eventDetailsPopover.anchorPosition)}
          disableScrollLock
          anchorReference="anchorPosition"
          anchorPosition={eventDetailsPopover.anchorPosition || { top: 0, left: 0 }}
          onClose={handleCloseEventDetailsPopover}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          classes={{ paper: "dashboard-calendar-details-popover" }}
        >
          {eventDetailsPopover.event && (
            <div className="dashboard-calendar-details-popover-content">
              <div className="dashboard-calendar-details-popover-header">
                <div>
                  <Typography variant="h6" className="dashboard-calendar-details-popover-title">
                    {getFormattedTitleWithCategory(eventDetailsPopover.event)}
                  </Typography>
                  <Typography variant="body2" className="dashboard-calendar-details-popover-date">
                    {formatEventTooltip(eventDetailsPopover.event).split("\n").slice(1).join(" | ")}
                  </Typography>
                  {(() => {
                    const desc = eventDetailsPopover.event.resource?.Description || eventDetailsPopover.event.resource?.description;
                    if (!desc) return null;
                    const cat = String(eventDetailsPopover.event.category || eventDetailsPopover.event.resource?.Category || "").toLowerCase();
                    const isLeave = cat === "leave";
                    return (
                      <Typography variant="body2" style={{ marginTop: "4px", color: "#555", wordBreak: "break-word" }}>
                        {isLeave ? `Reason: ${desc}` : desc}
                      </Typography>
                    );
                  })()}
                </div>
                <IconButton size="small" onClick={handleCloseEventDetailsPopover} className="dashboard-calendar-details-popover-close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
              {(canEditSelectedEvent || canDeleteSelectedEvent) && (
                <div className="dashboard-calendar-details-popover-actions">
                  {canEditSelectedEvent && (
                    <Button
                      size="small"
                      variant="outlined"
                      className="dashboard-calendar-details-popover-btn dashboard-calendar-details-popover-btn-edit"
                      aria-label="Edit event"
                      onClick={handleEditSelectedEvent}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </Button>
                  )}
                  {canDeleteSelectedEvent && (
                    <Button
                      size="small"
                      variant="outlined"
                      className="dashboard-calendar-details-popover-btn dashboard-calendar-details-popover-btn-delete"
                      aria-label="Delete event"
                      onClick={handleDeleteSelectedEvent}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </Button>
                  )}
                </div>
              )}

              {isAdmin && String(eventDetailsPopover.event.category || "").toLowerCase() === "leave" && (
                <div className="dashboard-calendar-leave-admin-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center', justifyContent: 'center', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                  <Tooltip title="Approve" placement="top" arrow>
                    <IconButton
                      style={{ cursor: "pointer", padding: "4px" }}
                      onClick={() => {
                        const leaveId = getEventDeleteMeta(eventDetailsPopover.event.resource, "leave")?.id || eventDetailsPopover.event.resource?.id;
                        handleCloseEventDetailsPopover();
                        if (typeof onUpdateLeaveStatus === "function" && leaveId) {
                          onUpdateLeaveStatus(leaveId, "Approved");
                        }
                      }}
                    >
                      <FontAwesomeIconComponent
                        classes="far fa-check-square"
                        style={{ cursor: "pointer", color: "var(--hrms-primary-blue-end, #036dda)" }}
                        fontSize="small"
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Declined" placement="top" arrow>
                    <IconButton
                      style={{ cursor: "pointer", padding: "4px" }}
                      onClick={() => {
                        const leaveId = getEventDeleteMeta(eventDetailsPopover.event.resource, "leave")?.id || eventDetailsPopover.event.resource?.id;
                        handleCloseEventDetailsPopover();
                        if (typeof onUpdateLeaveStatus === "function" && leaveId) {
                          onUpdateLeaveStatus(leaveId, "Declined");
                        }
                      }}
                    >
                      <FontAwesomeIconComponent
                        classes="fas fa-window-close"
                        style={{ cursor: "pointer", color: "var(--hrms-primary-blue-end, #036dda)" }}
                        fontSize="small"
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Unapproved" placement="top" arrow>
                    <IconButton
                      style={{ cursor: "pointer", padding: "4px" }}
                      onClick={() => {
                        const leaveId = getEventDeleteMeta(eventDetailsPopover.event.resource, "leave")?.id || eventDetailsPopover.event.resource?.id;
                        handleCloseEventDetailsPopover();
                        if (typeof onUpdateLeaveStatus === "function" && leaveId) {
                          onUpdateLeaveStatus(leaveId, "Unapproved");
                        }
                      }}
                    >
                      <FontAwesomeIconComponent
                        classes="fas fa-times-circle"
                        style={{ cursor: "pointer", color: "#ff0000" }}
                        fontSize="small"
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Cancel Leave Request" placement="top" arrow>
                    <IconButton
                      style={{ cursor: "pointer", padding: "4px" }}
                      onClick={() => {
                        const leaveId = getEventDeleteMeta(eventDetailsPopover.event.resource, "leave")?.id || eventDetailsPopover.event.resource?.id;
                        handleCloseEventDetailsPopover();
                        if (typeof onUpdateLeaveStatus === "function" && leaveId) {
                          onUpdateLeaveStatus(leaveId, "Cancelled");
                        }
                      }}
                    >
                      <FontAwesomeIconComponent
                        classes="fa fa-ban"
                        style={{ cursor: "pointer", color: "#dc3545" }}
                        fontSize="small"
                      />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>
          )}
        </Popover>

        <Popover
          open={Boolean(addPopover.anchorPosition)}
          disableScrollLock
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          anchorReference="anchorPosition"
          anchorPosition={addPopover.anchorPosition || { top: 0, left: 0 }}
          onClose={handleCloseAddPopover}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          classes={{ paper: `dashboard-calendar-add-popover is-${addTab}-tab` }}
        >
          <div
            className="dashboard-calendar-add-popover-content"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <IconButton
              size="small"
              onClick={handleCloseAddPopover}
              className="dashboard-calendar-add-popover-close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <div className="dashboard-calendar-add-popover-body">
              {isAdmin ? (
                <div className="dashboard-calendar-add-tabs">
                  <button
                    type="button"
                    className={`dashboard-calendar-add-tab ${addTab === "title" ? "is-active" : ""}`}
                    onClick={() => addPopover.mode !== "edit" && setAddTab("title")}
                    disabled={addPopover.mode === "edit" && addTab !== "title"}
                    style={addPopover.mode === "edit" && addTab !== "title" ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                  >
                    Title
                  </button>
                  <button
                    type="button"
                    className={`dashboard-calendar-add-tab ${addTab === "meeting" ? "is-active" : ""}`}
                    onClick={() => addPopover.mode !== "edit" && setAddTab("meeting")}
                    disabled={addPopover.mode === "edit" && addTab !== "meeting"}
                    style={addPopover.mode === "edit" && addTab !== "meeting" ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                  >
                    Meeting
                  </button>
                  <button
                    type="button"
                    className={`dashboard-calendar-add-tab ${addTab === "event" ? "is-active" : ""}`}
                    onClick={() => addPopover.mode !== "edit" && setAddTab("event")}
                    disabled={addPopover.mode === "edit" && addTab !== "event"}
                    style={addPopover.mode === "edit" && addTab !== "event" ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                  >
                    Event
                  </button>
                  <button
                    type="button"
                    className={`dashboard-calendar-add-tab ${addTab === "holiday" ? "is-active" : ""}`}
                    onClick={() => addPopover.mode !== "edit" && setAddTab("holiday")}
                    disabled={addPopover.mode === "edit" && addTab !== "holiday"}
                    style={addPopover.mode === "edit" && addTab !== "holiday" ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                  >
                    Holiday
                  </button>
                  <button
                    type="button"
                    className={`dashboard-calendar-add-tab ${addTab === "leave" ? "is-active" : ""}`}
                    onClick={() => addPopover.mode !== "edit" && setAddTab("leave")}
                    disabled={addPopover.mode === "edit" && addTab !== "leave"}
                    style={addPopover.mode === "edit" && addTab !== "leave" ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                  >
                    Leave
                  </button>
                  <button
                    type="button"
                    className={`dashboard-calendar-add-tab ${addTab === "timeEntry" ? "is-active" : ""}`}
                    onClick={() => addPopover.mode !== "edit" && setAddTab("timeEntry")}
                    disabled={addPopover.mode === "edit" && addTab !== "timeEntry"}
                    style={addPopover.mode === "edit" && addTab !== "timeEntry" ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                  >
                    Time Entry
                  </button>
                </div>
              ) : (
                <div className="dashboard-calendar-add-tabs">
                  <button
                    type="button"
                    className={`dashboard-calendar-add-tab ${addTab === "title" ? "is-active" : ""}`}
                    onClick={() => addPopover.mode !== "edit" && setAddTab("title")}
                    disabled={addPopover.mode === "edit" && addTab !== "title"}
                    style={addPopover.mode === "edit" && addTab !== "title" ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                  >
                    Title
                  </button>
                  <button
                    type="button"
                    className={`dashboard-calendar-add-tab ${addTab === "leave" ? "is-active" : ""}`}
                    onClick={() => addPopover.mode !== "edit" && setAddTab("leave")}
                    disabled={addPopover.mode === "edit" && addTab !== "leave"}
                    style={addPopover.mode === "edit" && addTab !== "leave" ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                  >
                    Leave
                  </button>
                  <button
                    type="button"
                    className={`dashboard-calendar-add-tab ${addTab === "timeEntry" ? "is-active" : ""}`}
                    onClick={() => addPopover.mode !== "edit" && setAddTab("timeEntry")}
                    disabled={addPopover.mode === "edit" && addTab !== "timeEntry"}
                    style={addPopover.mode === "edit" && addTab !== "timeEntry" ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                  >
                    Time Entry
                  </button>
                </div>
              )}
              {addTab === "event" ? (
                <div className="dashboard-calendar-event-form-wrap" style={{ padding: "0" }}>
                  <Typography
                    variant="h6"
                    style={{
                      color: "#036dda",
                      fontWeight: 700,
                      fontSize: "1.15rem",
                      margin: "0 0 6px 0",
                    }}
                  >
                    {addPopover.mode === "edit" ? "Edit Upcoming Event" : "Add Upcoming Event"}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="upcomingEventName"
                        name="upcomingEventName"
                        label="Event Name"
                        variant="outlined"
                        value={addEventTitle}
                        onChange={(e) => setAddEventTitle(e.target.value)}
                        size="small"
                        InputLabelProps={{ style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="upcomingEventdate"
                        name="upcomingEventdate"
                        label="Event Date"
                        type="date"
                        variant="outlined"
                        value={eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "")}
                        onChange={(e) => setEventDate(e.target.value)}
                        InputLabelProps={{ shrink: true, style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        id="eventDetails"
                        name="eventDetails"
                        label="Event Details"
                        variant="outlined"
                        value={eventDescription}
                        onChange={(e) => setEventDescription(e.target.value)}
                        InputLabelProps={{ style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <div
                        className={`common-upload-area ${dragActive ? 'is-active' : ''}`}
                        style={{
                          width: '100%',
                          minHeight: '92px',
                          border: '2px dashed #cbd5e1',
                          borderRadius: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '8px',
                          boxSizing: 'border-box',
                        }}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => {
                          const el = document.getElementById('contained-button-file-event');
                          if (el) el.click();
                        }}
                      >
                        <input
                          accept="image/*"
                          id="contained-button-file-event"
                          type="file"
                          onChange={uploadSingleFile}
                          style={{ display: 'none' }}
                        />
                        {eventImageUrl && (
                          <img
                            src={eventImageUrl}
                            alt="Event preview"
                            style={{
                              width: '60px',
                              height: '60px',
                              marginBottom: '4px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                            }}
                          />
                        )}
                        <Typography variant="subtitle2" style={{ color: '#036dda', fontSize: '0.85rem', fontWeight: 600 }}>
                          {eventImageUrl ? "Change File" : "Drag & Drop your file here"}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#64748b', fontSize: '0.75rem' }}>
                          or
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          component="span"
                          size="small"
                          startIcon={<CloudUploadIcon />}
                          style={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            fontWeight: 600,
                            color: '#036dda',
                            borderColor: '#036dda',
                            marginTop: '4px',
                            fontSize: '0.78rem',
                          }}
                        >
                          Upload a file
                        </Button>
                      </div>
                      {eventImageUrl && (
                        <div style={{ textAlign: 'center', marginTop: '4px' }}>
                          <Button onClick={removeImg} size="small" color="secondary" style={{ textTransform: 'none', fontSize: '0.75rem' }}>
                            Remove Image
                          </Button>
                        </div>
                      )}
                    </Grid>
                  </Grid>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSaveQuickEvent();
                      }}
                      style={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, padding: '6px 20px', background: '#036dda' }}
                    >
                      {addPopover.mode === "edit" ? "Update" : "Save"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCloseAddPopover}
                      style={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, color: '#475569', borderColor: '#cbd5e1' }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : addTab === "holiday" ? (
                <div className="dashboard-calendar-holiday-form-wrap" style={{ padding: "0" }}>
                  <Typography
                    variant="h6"
                    style={{
                      color: "#036dda",
                      fontWeight: 700,
                      fontSize: "1.15rem",
                      margin: "0 0 6px 0",
                    }}
                  >
                    {addPopover.mode === "edit" ? "Edit Holiday" : "Add Holiday"}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="holidayName"
                        name="holidayName"
                        label="Holiday Name"
                        variant="outlined"
                        value={addEventTitle}
                        onChange={(e) => setAddEventTitle(e.target.value)}
                        size="small"
                        InputLabelProps={{ style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="holidayDate"
                        name="holidayDate"
                        label="Holiday Date"
                        type="date"
                        variant="outlined"
                        value={eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "")}
                        onChange={(e) => setEventDate(e.target.value)}
                        InputLabelProps={{ shrink: true, style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSaveQuickEvent();
                      }}
                      style={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, padding: '6px 20px', background: '#036dda' }}
                    >
                      {addPopover.mode === "edit" ? "Update" : "Save"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCloseAddPopover}
                      style={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, color: '#475569', borderColor: '#cbd5e1' }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : addTab === "leave" ? (
                <div className="dashboard-calendar-leave-form-wrap" style={{ padding: "0" }}>
                  <Typography
                    variant="h6"
                    style={{
                      color: "#036dda",
                      fontWeight: 700,
                      fontSize: "1.15rem",
                      margin: "0 0 10px 0",
                    }}
                  >
                    {addPopover.mode === "edit" ? "Edit Leave" : "Add Leave"}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel id="leave-type-label" style={{ fontWeight: 400 }}>Leave-Type</InputLabel>
                        <Select
                          labelId="leave-type-label"
                          id="leaveType"
                          label="Leave-Type"
                          value={leaveType}
                          onChange={(e) => setLeaveType(e.target.value)}
                          style={{ fontWeight: 400 }}
                        >
                          {LEAVE_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="startDate"
                        name="startDate"
                        label="Start-date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={leaveStartDate || eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "")}
                        onChange={(e) => handleLeaveStartDateChange(e.target.value)}
                        error={Boolean(leaveFormErrors.startDate)}
                        helperText={leaveFormErrors.startDate}
                        inputProps={{
                          max: leaveEndDate || undefined,
                        }}
                        InputLabelProps={{ shrink: true, style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel id="first-half-label" style={{ fontWeight: 400 }}>Select-Half</InputLabel>
                        <Select
                          labelId="first-half-label"
                          id="firstleavetype"
                          label="Select-Half"
                          value={firstLeaveHalf}
                          onChange={(e) => setFirstLeaveHalf(e.target.value)}
                          style={{ fontWeight: 400 }}
                        >
                          <MenuItem value="first-half">First Half</MenuItem>
                          <MenuItem value="second-half">Second Half</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="endDate"
                        name="endDate"
                        label="End-date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={leaveEndDate || leaveStartDate || eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "")}
                        onChange={(e) => handleLeaveEndDateChange(e.target.value)}
                        error={Boolean(leaveFormErrors.endDate)}
                        helperText={leaveFormErrors.endDate}
                        inputProps={{
                          min: leaveStartDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : undefined),
                        }}
                        InputLabelProps={{ shrink: true, style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel id="second-half-label" style={{ fontWeight: 400 }}>Select-Half</InputLabel>
                        <Select
                          labelId="second-half-label"
                          id="secondleavetype"
                          label="Select-Half"
                          value={secondLeaveHalf}
                          onChange={(e) => setSecondLeaveHalf(e.target.value)}
                          style={{ fontWeight: 400 }}
                        >
                          <MenuItem value="first-half">First Half</MenuItem>
                          <MenuItem value="second-half">Second Half</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        id="ticketMessage"
                        name="ticketMessage"
                        label="Reason for Leave"
                        variant="outlined"
                        value={addEventDescription}
                        onChange={(e) => {
                          setAddEventDescription(e.target.value);
                          setLeaveFormErrors((prev) => ({ ...prev, description: null }));
                        }}
                        error={Boolean(leaveFormErrors.description)}
                        helperText={leaveFormErrors.description}
                        placeholder="Enter Reason for Leave"
                        InputLabelProps={{ style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                      />
                    </Grid>

                  </Grid>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSaveQuickEvent();
                      }}
                      style={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, padding: '6px 20px', background: '#036dda' }}
                    >
                      {addPopover.mode === "edit" ? "Update" : "Save"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCloseAddPopover}
                      style={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, color: '#475569', borderColor: '#cbd5e1' }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : addTab === "timeEntry" ? (
                <div className="dashboard-calendar-timeentry-form-wrap" style={{ padding: "0" }}>
                  <Typography
                    variant="h6"
                    style={{
                      color: "#036dda",
                      fontWeight: 700,
                      fontSize: "1.15rem",
                      margin: "0 0 10px 0",
                    }}
                  >
                    {addPopover.mode === "edit" ? "Edit Time Entry" : "Add Time Entry"}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="timeEntryDate"
                        name="timeEntryDate"
                        label="Date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={eventDate || (addPopover.start ? moment(addPopover.start).format("YYYY-MM-DD") : "")}
                        onChange={(e) => setEventDate(e.target.value)}
                        InputLabelProps={{ shrink: true, style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="startTime"
                        label="Start-time"
                        variant="outlined"
                        type="time"
                        name="startTime"
                        size="small"
                        value={startTime || "07:30"}
                        onChange={(e) => setStartTime(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                          style: { fontWeight: 400 }
                        }}
                        InputProps={{ style: { fontWeight: 400 } }}
                        inputProps={{
                          step: 300,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="endTime"
                        label="End-time"
                        variant="outlined"
                        type="time"
                        name="endTime"
                        size="small"
                        value={endTime || "18:00"}
                        onChange={(e) => setEndTime(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                          style: { fontWeight: 400 }
                        }}
                        InputProps={{ style: { fontWeight: 400 } }}
                        inputProps={{
                          step: 300,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        id="timeEntryReason"
                        name="timeEntryReason"
                        label="Reason"
                        variant="outlined"
                        value={addEventDescription}
                        onChange={(e) => setAddEventDescription(e.target.value)}
                        placeholder="Enter Reason"
                        InputLabelProps={{ style: { fontWeight: 400 } }}
                        InputProps={{ style: { fontWeight: 400 } }}
                      />
                    </Grid>
                  </Grid>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSaveQuickEvent();
                      }}
                      style={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, padding: '6px 20px', background: '#036dda' }}
                    >
                      {addPopover.mode === "edit" ? "Update" : "Save"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCloseAddPopover}
                      style={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, color: '#475569', borderColor: '#cbd5e1' }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="dashboard-calendar-add-popover-title-wrap">
                    <input
                      type="text"
                      placeholder={addTab === "leave" ? "Add leave" : addTab === "timeEntry" || addTab === "time" ? "Add time entry" : "Add title"}
                      className="dashboard-calendar-add-popover-input"
                      value={addEventTitle}
                      onChange={(e) => setAddEventTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSaveQuickEvent();
                        }
                      }}
                      autoFocus
                    />
                    <textarea
                      placeholder="Add description (optional)"
                      className="dashboard-calendar-add-popover-textarea"
                      value={addEventDescription}
                      onChange={(e) => setAddEventDescription(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div className="dashboard-calendar-add-popover-meta-card" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div className="dashboard-calendar-add-popover-date-row">
                        <CalendarTodayIcon className="dashboard-calendar-add-popover-date-icon" />
                        <div className="dashboard-calendar-add-popover-date-copy">
                          <span className="dashboard-calendar-add-popover-date-text" style={{ fontSize: '15px' }}>
                            {addPopover.formattedDate}
                          </span>
                        </div>
                      </div>
                      <div className="dashboard-calendar-add-popover-allday-row" style={{ marginTop: '12px' }}>
                        <label className="dashboard-calendar-add-popover-checkbox-label">
                          <input
                            type="checkbox"
                            checked={allDay}
                            onChange={(e) => setAllDay(e.target.checked)}
                            className="dashboard-calendar-add-popover-checkbox"
                          />
                          <span>All Day</span>
                        </label>
                      </div>
                    </div>

                    {isAdmin && (
                      <div className="dashboard-calendar-add-popover-users-row" style={{ marginLeft: "12px" }}>
                        <TextField
                          className="attendance-header-field"
                          style={{ width: '200px' }}
                          label="Select User"
                          variant="outlined"
                          value={
                            targetUsers.length === 0 
                              ? "" 
                              : targetUsers.length === (allUsers || []).length 
                                ? "All Users" 
                                : `${targetUsers.length} Users Selected`
                          }
                          onClick={(e) => setUserPopoverAnchorEl(e.currentTarget)}
                          InputProps={{
                            readOnly: true,
                            endAdornment: <ExpandMoreIcon style={{ color: '#036dda', cursor: 'pointer' }} />,
                            style: { cursor: 'pointer', color: 'rgba(0, 0, 0, 0.87)', fontWeight: 400 }
                          }}
                          InputLabelProps={{
                            shrink: targetUsers.length > 0 || Boolean(userPopoverAnchorEl),
                            style: (targetUsers.length > 0 || Boolean(userPopoverAnchorEl)) 
                              ? { color: 'rgba(0, 0, 0, 0.87)', backgroundColor: 'rgb(255, 255, 255)', padding: '0px 4px', fontWeight: 400 }
                              : { color: 'rgba(0, 0, 0, 0.87)', fontWeight: 400 }
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {!allDay && (
                    <div className="dashboard-calendar-add-popover-time-row" style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          id="popover-start-time"
                          label="Start Time"
                          variant="outlined"
                          type="time"
                          value={formatTimeInputValue(startTime, "09:00")}
                          onChange={(e) => setStartTime(e.target.value)}
                          InputLabelProps={{
                            shrink: true,
                            style: { color: '#036dda', fontWeight: 600 }
                          }}
                          inputProps={{
                            step: 300,
                          }}
                          size="small"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          id="popover-end-time"
                          label="End Time"
                          variant="outlined"
                          type="time"
                          value={formatTimeInputValue(endTime, "18:00")}
                          onChange={(e) => setEndTime(e.target.value)}
                          InputLabelProps={{
                            shrink: true,
                            style: { color: '#036dda', fontWeight: 600 }
                          }}
                          inputProps={{
                            step: 300,
                          }}
                          size="small"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {addTab !== "event" && addTab !== "holiday" && addTab !== "leave" && addTab !== "timeEntry" && (
              <div className="dashboard-calendar-add-popover-actions">
                <Button
                  size="small"
                  className="dashboard-calendar-add-popover-btn-save"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSaveQuickEvent();
                  }}
                  variant="contained"
                  color="primary"
                >
                  {addPopover.mode === "edit" ? "Update" : "Save"}
                </Button>
              </div>
            )}
          </div>
        </Popover>

        <Popover
          open={Boolean(userPopoverAnchorEl)}
          anchorEl={userPopoverAnchorEl}
          onClose={() => { setUserPopoverAnchorEl(null); setUserSearchQuery(""); }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          disableScrollLock
          PaperProps={{ style: { borderRadius: '12px', overflow: 'hidden', minWidth: '240px', marginTop: '4px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: '240px' }}>
            <div style={{ backgroundColor: '#036dda', padding: '12px 14px' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', marginBottom: '10px' }}>Select User</div>
              <div style={{ position: 'relative' }}>
                <SearchIcon style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#036dda', fontSize: '18px' }} />
                <input 
                  type="text" 
                  placeholder="Search users..." 
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  style={{ width: '100%', padding: '6px 8px 6px 32px', borderRadius: '20px', border: 'none', outline: 'none', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '8px 0', backgroundColor: '#fff' }}>
              <div 
                style={{ display: 'flex', alignItems: 'center', padding: '6px 14px', cursor: 'pointer' }}
                onClick={() => {
                  if (targetUsers.length === (allUsers || []).length && (allUsers || []).length > 0) {
                    setTargetUsers([]);
                  } else {
                    setTargetUsers((allUsers || []).map(u => ({ value: u.id, label: (u.firstName ? `${u.firstName} ${u.lastName || ''}` : (u.name || u.email || `User ${u.id}`)).trim() })));
                  }
                }}
              >
                <Checkbox 
                  checked={targetUsers.length === (allUsers || []).length && (allUsers || []).length > 0} 
                  size="small" 
                  style={{ padding: '0 8px 0 0', pointerEvents: 'none' }} 
                  color="primary"
                />
                <span style={{ fontSize: '14px', color: '#333', fontWeight: 600 }}>All Users</span>
              </div>
              
              {(allUsers || [])
                .filter(u => (u.firstName ? `${u.firstName} ${u.lastName || ''}` : (u.name || u.email || `User ${u.id}`)).toLowerCase().includes(userSearchQuery.toLowerCase()))
                .map(u => {
                  const isChecked = targetUsers.some(t => t.value === u.id);
                  const userName = (u.firstName ? `${u.firstName} ${u.lastName || ''}` : (u.name || u.email || `User ${u.id}`)).trim();
                  return (
                    <div 
                      key={u.id}
                      style={{ display: 'flex', alignItems: 'center', padding: '6px 14px', cursor: 'pointer' }}
                      onClick={() => {
                        if (isChecked) {
                          setTargetUsers(targetUsers.filter(t => t.value !== u.id));
                        } else {
                          setTargetUsers([...targetUsers, { value: u.id, label: userName }]);
                        }
                      }}
                    >
                      <Checkbox 
                        checked={isChecked} 
                        size="small" 
                        style={{ padding: '0 8px 0 0', pointerEvents: 'none' }} 
                        color="primary"
                      />
                      <span style={{ fontSize: '14px', color: '#333' }}>{userName}</span>
                    </div>
                  );
                })
              }
            </div>
          </div>
        </Popover>
      </CardContent>
    </Card>
  );
};

export default DashboardCalendar;



